import { useAuth } from '@eg/compartido'
import { LogOut, ShoppingBag } from 'lucide-react'
import { lazy, Suspense, type ReactNode } from 'react'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import { es } from '../content/es'
import { BotonIdioma } from '../content/idioma'
import { esDemo } from '../demo/modo'
import { useCarrito } from '../lib/carrito'
import { ElegirPassword } from '../features/acceso/Ingresar'
import { GuiaVisual } from './GuiaVisual'
import { NavPildora } from './NavPildora'

// Cada pantalla se descarga al entrar a ella: la portada trae animaciones y las de empresa, formularios y zod.
const Portada = lazy(() => import('../features/inicio/Portada').then((m) => ({ default: m.Portada })))
const Ayuda = lazy(() => import('../features/inicio/Ayuda').then((m) => ({ default: m.Ayuda })))
const Ingresar = lazy(() => import('../features/acceso/Ingresar').then((m) => ({ default: m.Ingresar })))
const Catalogo = lazy(() => import('../features/catalogo/Catalogo').then((m) => ({ default: m.Catalogo })))
const DetalleProducto = lazy(() => import('../features/catalogo/DetalleProducto').then((m) => ({ default: m.DetalleProducto })))
const RegistroEmpresa = lazy(() => import('../features/empresa/RegistroEmpresa').then((m) => ({ default: m.RegistroEmpresa })))
const Carrito = lazy(() => import('../features/carrito/Carrito').then((m) => ({ default: m.Carrito })))
const Checkout = lazy(() => import('../features/pedidos/Checkout').then((m) => ({ default: m.Checkout })))
const MisPedidos = lazy(() => import('../features/pedidos/Pedidos').then((m) => ({ default: m.MisPedidos })))
const PedidoDetalle = lazy(() => import('../features/pedidos/Pedidos').then((m) => ({ default: m.PedidoDetalle })))
const MiEmpresa = lazy(() => import('../features/empresa/MiEmpresa').then((m) => ({ default: m.MiEmpresa })))

// La barra de la demostración solo existe en esa compilación: en la tienda real esta rama se elimina.
const BarraDemo = esDemo ? lazy(() => import('../demo/BarraDemo').then((m) => ({ default: m.BarraDemo }))) : null

function Cabecera() {
  const { sesion, cerrarSesion } = useAuth()
  const { unidades } = useCarrito()
  const items = [
    { label: es.nav.productos, href: '/catalogo' },
    ...(sesion ? [{ label: es.nav.pedidos, href: '/pedidos' }] : []),
    { label: es.nav.miEmpresa, href: '/mi-empresa' },
    ...(sesion ? [] : [{ label: es.nav.ingresar, href: '/ingresar' }]),
  ]
  return (
    <header className="encabezado">
      <div className="contenedor encabezado__fila">
        <Link className="marca" to="/" aria-label={`${es.marca}, ${es.lema}. Ir al inicio`}>
          <span className="marca__nombre">
            {es.marca}
            <sup className="marca__tm" aria-hidden="true">
              {es.marcaMarca}
            </sup>
          </span>
          <span className="marca__lema">{es.lema}</span>
        </Link>
        <div className="encabezado__acciones">
          <NavPildora items={items} />
          <BotonIdioma />
          <Link className="carrito-enlace" to="/carrito" aria-label={`${es.nav.carrito}: ${unidades} ${unidades === 1 ? 'unidad' : 'unidades'}`}>
            <ShoppingBag className="icono" strokeWidth={1.75} aria-hidden="true" />
            {unidades > 0 && <span className="carrito-enlace__cuenta">{unidades}</span>}
          </Link>
          {sesion && (
            <button type="button" className="boton boton--secundario boton--compacto" onClick={() => void cerrarSesion()}>
              <LogOut className="icono" strokeWidth={1.75} aria-hidden="true" />
              {es.nav.salir}
            </button>
          )}
        </div>
      </div>
    </header>
  )
}

function Pie() {
  return (
    <footer className="pie">
      <div className="contenedor pie__fila">
        <div>
          <p className="pie__marca">{es.marca}</p>
          <p>{es.pie}</p>
        </div>
        <nav aria-label={es.pieNav}>
          <ul>
            <li>
              <Link to="/catalogo">{es.pieEnlaces.productos}</Link>
            </li>
            <li>
              <Link to="/registro-empresa">{es.pieEnlaces.registro}</Link>
            </li>
            <li>
              <Link to="/ayuda">{es.pieEnlaces.ayuda}</Link>
            </li>
            <li>
              <Link to="/ingresar">{es.nav.ingresar}</Link>
            </li>
          </ul>
        </nav>
      </div>
    </footer>
  )
}

function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const esPortada = pathname === '/' || pathname === '/ayuda'
  return (
    <div className="pagina">
      <a className="saltar-al-contenido" href="#contenido">
        {es.saltar}
      </a>
      {BarraDemo && (
        <Suspense fallback={null}>
          <BarraDemo />
        </Suspense>
      )}
      <Cabecera />
      <main id="contenido" className={esPortada ? 'principal principal--portada' : 'principal contenedor'} tabIndex={-1}>
        <div key={pathname} className="transicion-pagina">
          {children}
        </div>
      </main>
      <Pie />
    </div>
  )
}

function NoEncontrada() {
  return (
    <>
      <h1 className="titulo-seccion">{es.noEncontrada.titulo}</h1>
      <p className="seccion__intro">{es.noEncontrada.texto}</p>
      <p className="accion">
        <Link className="boton boton--primario" to="/">
          {es.noEncontrada.volver}
        </Link>
      </p>
    </>
  )
}

export function App() {
  const { recuperando, sesion } = useAuth()
  // Tras abrir el enlace de «Olvidé mi contraseña» hay que elegir una nueva antes de seguir.
  if (recuperando && sesion) {
    return (
      <Layout>
        <ElegirPassword />
      </Layout>
    )
  }
  return (
    <Layout>
      <Suspense
        fallback={
          <div role="status" aria-live="polite">
            <div className="esqueleto" />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Portada />} />
          <Route path="/ayuda" element={<Ayuda />} />
          <Route path="/catalogo" element={<Catalogo esPagina />} />
          <Route path="/producto/:slug" element={<DetalleProducto />} />
          <Route path="/carrito" element={<Carrito />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/pedidos" element={<MisPedidos />} />
          <Route path="/pedido/:id" element={<PedidoDetalle />} />
          <Route path="/ingresar" element={<Ingresar />} />
          <Route path="/registro-empresa" element={<RegistroEmpresa />} />
          <Route path="/mi-empresa" element={<MiEmpresa />} />
          {import.meta.env.DEV && <Route path="/guia" element={<GuiaVisual />} />}
          <Route path="*" element={<NoEncontrada />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}
